import {useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {SlidersHorizontal,X} from 'lucide-react';
import {useHouse} from '../lib/house';
import {ProductCard,Loading,LoadError,Reveal,Cta} from '../components/Elements';
import {WorldsStrip} from './Home';

const FilterSelect = ({label,value,onChange,options}) => (
  <label>{label}
    <select data-testid={`filter-${label.toLowerCase()}`} value={value} onChange={e=>onChange(e.target.value)}>
      <option value="all" label={`Any ${label.toLowerCase()}`}/>
      {options.map(option=>{
        const [key,text]=Array.isArray(option)?option:[option,option];
        return <option key={key} value={key} label={text}/>;
      })}
    </select>
  </label>
);

export default function Collection(){
  const {products,worlds,loading,error,reload}=useHouse();
  const [params,setParams]=useSearchParams();
  const world=params.get('world')||'all';
  const [expert,setExpert]=useState(false),[filters,setFilters]=useState(false);
  const [family,setFamily]=useState('all'),[intensity,setIntensity]=useState('all');
  const [season,setSeason]=useState('all'),[occasion,setOccasion]=useState('all');
  const [mood,setMood]=useState('all'),[price,setPrice]=useState('all'),[query,setQuery]=useState('');
  const currentWorld=worlds.find(w=>w.id===world);
  const filtered=products.filter(p=>
    (world==='all'||p.world===world||p.secondary===world)&&
    (family==='all'||p.family===family)&&
    (season==='all'||p.season.includes(season))&&
    (occasion==='all'||p.occasion.includes(occasion))&&
    (mood==='all'||p.mood===mood)&&
    (price==='all'||p.price<=Number(price))&&
    (intensity==='all'||(intensity==='soft'?p.spectrum.intensity<=2:intensity==='balanced'?p.spectrum.intensity===3:p.spectrum.intensity>=4))&&
    (!query||[p.name,...p.opening,...p.heart,...p.drydown].join(' ').toLowerCase().includes(query.toLowerCase()))
  );
  const reset=()=>{setParams({});setFamily('all');setIntensity('all');setSeason('all');setOccasion('all');setMood('all');setPrice('all');setQuery('');};
  const filterFields=[
    ['Family',family,setFamily,[...new Set(products.map(p=>p.family))]],
    ['Intensity',intensity,setIntensity,[['soft','Close to skin'],['balanced','A quiet presence'],['bold','A lasting trail']]],
    ['Season',season,setSeason,['Spring','Summer','Autumn','Winter']],
    ['Occasion',occasion,setOccasion,['Everyday','Work','Date','Travel','Celebration','Formal','Personal ritual']],
    ['Mood',mood,setMood,products.map(p=>p.mood)],
    ['Price',price,setPrice,[['150','Up to €150'],['175','Up to €175'],['200','Up to €200']]],
  ];
  return <div className="collection-page" data-testid="collection-page">
    <section className="collection-intro section-pad">
      <Reveal>
        <span className="eyebrow">{currentWorld?`WORLD ${currentWorld.number} / THE COLLECTION`:'SEVEN COMPOSITIONS. NO CONVENTIONS.'}</span>
        <h1 data-testid="collection-title">{currentWorld?currentWorld.name:'The collection.'}</h1>
        <p>{currentWorld?currentWorld.line:'Not a catalogue. A collection of stories.'}<br/>{currentWorld?currentWorld.character:'Find the one that sounds a little like yours.'}</p>
      </Reveal>
      <div className="collection-intro-side"><span className="little-star">✳</span><p>Genderless by nature.<br/>Personal by instinct.</p><span className="concept-chip" data-testid="collection-concept">THE CONCEPT EDITION</span></div>
    </section>
    <section className="collection-content section-pad">
      <div className="collection-toolbar">
        <div className="world-tabs" role="group" aria-label="Scent world">
          {[{id:'all',name:'All worlds'},...worlds].map(w=><button data-testid={`filter-world-${w.id}`} key={w.id} className={world===w.id?'active':''} aria-pressed={world===w.id} onClick={()=>setParams(w.id==='all'?{}:{world:w.id})}>{w.name}</button>)}
        </div>
        <button data-testid="filter-toggle" className="filter-toggle" onClick={()=>setFilters(!filters)} aria-expanded={filters}><SlidersHorizontal size={15}/>Refine</button>
      </div>
      {filters&&<div className="expanded-filters" data-testid="expanded-filters">
        {filterFields.map(([label,value,onChange,options])=><FilterSelect key={label} label={label} value={value} onChange={onChange} options={options}/>)}
        <label>Search by note<input data-testid="filter-notes" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Sandalwood, iris…"/></label>
        <button data-testid="reset-filters" className="text-button" onClick={reset}>Reset<X size={14}/></button>
      </div>}
      <div className="collection-sub-toolbar">
        <span data-testid="collection-count">{filtered.length} {filtered.length===1?'composition':'compositions'}</span>
        <div className="knowledge-toggle"><button data-testid="mode-exploring" className={!expert?'active':''} onClick={()=>setExpert(false)}>I'm just exploring</button><span>/</span><button data-testid="mode-expert" className={expert?'active':''} onClick={()=>setExpert(true)}>I know perfume</button></div>
      </div>
      {loading?<Loading/>:error?<LoadError retry={reload}/>:filtered.length?
        <div className="product-grid">{filtered.map(p=><Reveal key={p.id}><ProductCard product={p} expert={expert}/></Reveal>)}</div>:
        <div className="empty-state" data-testid="collection-empty"><h2>A different path, perhaps.</h2><p>No compositions match these choices. Leave a little more room for discovery.</p><Cta onClick={reset} id="empty-reset">Reset your choices</Cta></div>
      }
      <div className="collection-note"><p>These are concept compositions, not products available for sale. Notes describe creative directions; prices are indicative.</p><Cta to="/discover" id="collection-discover" outline>Let instinct guide you</Cta></div>
    </section>
  </div>;
}

export function Worlds(){return <><div className="page-lead"><span className="eyebrow">NO GENDER. NO AGE. NO EXPECTATIONS.</span><h1>Find your <i>world.</i></h1><p>You can belong to more than one. In fact, we hope you do.</p></div><WorldsStrip full/></>;}